"use client";

import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { isNative } from "@/lib/capacitor";
import { env } from "@/lib/env";
import i18n from "@/lib/i18n";

type NotificationPermissionStatus =
  | "prompt"
  | "granted"
  | "denied"
  | "unsupported";

interface UsePushNotificationsReturn {
  /** Current notification permission status */
  permission: NotificationPermissionStatus;
  /** Whether the user has previously opted in (stored locally) */
  hasOptedIn: boolean;
  /** Whether the token has been registered with the backend */
  isRegistered: boolean;
  /** True while registering the push token */
  isLoading: boolean;
  /** Error message, if any */
  error: string | null;
  /** Request permission and register the push token */
  optIn: () => Promise<void>;
  /** Unregister the push token and revoke permission */
  optOut: () => Promise<void>;
}

// Keep a ref of registered tokens to avoid duplicate registrations
const _registeredTokens = new Set<string>();

/** Service worker handling `push` + `notificationclick` (public/sw.js). */
export const SW_URL = "/sw.js";
/** How long to wait for the worker to activate before giving up (M20). */
export const SW_READY_TIMEOUT_MS = 10_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

/**
 * Find (or register) the app's service worker and wait — bounded — until it
 * is active. Returns `null` when push cannot work in this browser (audit
 * M20: nothing registered a worker, so `ready` never resolved).
 */
async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return null;
  const container = navigator.serviceWorker;
  try {
    let registration: ServiceWorkerRegistration | undefined;
    if (typeof container.getRegistration === "function") {
      registration = await container.getRegistration();
    }
    if (!registration && typeof container.register === "function") {
      registration = await container.register(SW_URL);
    }
    // pushManager.subscribe() needs an *active* worker.
    const ready = await withTimeout(
      Promise.resolve(container.ready),
      SW_READY_TIMEOUT_MS,
    );
    if (ready) return ready;
    return registration?.active ? registration : null;
  } catch {
    return null;
  }
}

/** Push is web-only for now: native apps would need
 *  @capacitor/push-notifications (FCM/APNs), which is not wired yet. */
function pushUnsupported(): boolean {
  return isNative() || typeof Notification === "undefined";
}

/**
 * Hook to manage push notification subscription (Web Push API).
 *
 * On the web, registers `/sw.js` and subscribes through its PushManager
 * with the VAPID public key. On Capacitor native, push is reported as
 * "unsupported" until the native push plugin is integrated.
 *
 * The push subscription JSON is sent to the backend as the token string,
 * and stored in the `user_push_tokens` table.
 */
export function usePushNotifications(): UsePushNotificationsReturn {
  const [permission, setPermission] =
    useState<NotificationPermissionStatus>("prompt");
  const [hasOptedIn, setHasOptedIn] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registerToken = useCallback(async () => {
    try {
      if (!env.VAPID_PUBLIC_KEY) {
        setError(i18n.t("notifications.not_configured"));
        return;
      }

      const registration = await getServiceWorkerRegistration();
      if (!registration || !registration.pushManager) {
        setError("Push notifications not available (no service worker)");
        return;
      }

      // Subscribe to push
      let subscription: PushSubscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: env.VAPID_PUBLIC_KEY,
        });
      } catch {
        // Permission denied or push not available
        setError("Impossible de s'abonner aux notifications push");
        return;
      }

      const tokenStr = JSON.stringify(subscription.toJSON());

      // Avoid duplicate registrations
      if (_registeredTokens.has(tokenStr)) {
        setIsRegistered(true);
        return;
      }

      await api("/api/v1/notifications/register", {
        method: "POST",
        body: {
          platform: "web",
          token: tokenStr,
        },
      });

      _registeredTokens.add(tokenStr);
      setIsRegistered(true);
      setError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Erreur de notification";
      setError(message);
    }
  }, []);

  // Check current permission state on mount
  useEffect(() => {
    if (pushUnsupported()) {
      setPermission("unsupported");
      return;
    }
    setPermission(Notification.permission as NotificationPermissionStatus);

    // Check localStorage for previous opt-in
    const optedIn = localStorage.getItem("bc.push_opted_in") === "true";
    setHasOptedIn(optedIn);

    // If previously opted in and permission is still granted, try to re-register
    if (optedIn && Notification.permission === "granted") {
      void registerToken();
    }
  }, [registerToken]);

  const optIn = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (pushUnsupported()) {
        setPermission("unsupported");
        setError("Les notifications ne sont pas supportées sur cet appareil");
        return;
      }

      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermissionStatus);

      if (result === "granted") {
        localStorage.setItem("bc.push_opted_in", "true");
        setHasOptedIn(true);
        await registerToken();
      } else if (result === "denied") {
        localStorage.setItem("bc.push_opted_in", "false");
        setHasOptedIn(false);
        setError(
          "Notifications bloquées par le navigateur. " +
            "Vous pouvez les réactiver dans les paramètres du site.",
        );
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Erreur lors de l'inscription";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [registerToken]);

  const optOut = useCallback(async () => {
    setIsLoading(true);
    try {
      localStorage.setItem("bc.push_opted_in", "false");
      setHasOptedIn(false);

      // Unregister from backend
      try {
        await api("/api/v1/notifications/token/web", { method: "DELETE" });
      } catch {
        // Ignore backend errors during opt-out
      }

      // Unsubscribe any current push subscription (never registers a new worker)
      const container =
        typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
      if (container) {
        const registration =
          typeof container.getRegistration === "function"
            ? await container.getRegistration()
            : await withTimeout(
                Promise.resolve(container.ready),
                SW_READY_TIMEOUT_MS,
              );
        const subscription = await registration?.pushManager?.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      setIsRegistered(false);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    permission,
    hasOptedIn,
    isRegistered,
    isLoading,
    error,
    optIn,
    optOut,
  };
}
